import { useRef, useState, useEffect, useCallback } from "react";
import { useSnapshot } from "valtio";
import toast from "react-hot-toast";
import {
  tattoo,
  getActivePartKey,
  addLayerFromFile,
  addDecalFromFile,
  goPreview,
} from "../state";
import { getOwnShopProfile, upsertOwnShopProfile } from "../lib/supabaseClient";
import { APPEARANCE_OPTIONS } from "./constants";
import { useSubscription } from "../hooks/useSubscription";
import { ARTIST_TRIAL_DAYS } from "../billing/constants";

export function useLeftPanelState({ readOnly, isMobile }) {
  const snap = useSnapshot(tattoo);
  const partKey = getActivePartKey();

  const layerFileRef = useRef(null);
  const decalFileRef = useRef(null);
  const containerRef = useRef(null);

  const [showPaint, setShowPaint] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [showArtistWizard, setShowArtistWizard] = useState(false);
  const [showArtistUpgrade, setShowArtistUpgrade] = useState(false);
  const [shopProfile, setShopProfile] = useState(null);
  const [shopProfileLoading, setShopProfileLoading] = useState(true);
  const [preparedVideo, setPreparedVideo] = useState(null);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoPreset, setVideoPreset] = useState("push");

  const [skinOpen, setSkinOpen] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("tt_skin_open") || "true");
    } catch {
      return true;
    }
  });

  // fix: desktop previously read tt_skin_open for this key by mistake
  const [appearanceOpen, setAppearanceOpen] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("tt_appearance_open") || "true");
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("tt_appearance_open", JSON.stringify(appearanceOpen));
    } catch {}
  }, [appearanceOpen]);

  useEffect(() => {
    let cancelled = false;
    async function loadProfile() {
      try {
        const shop = await getOwnShopProfile();
        if (cancelled) return;
        setShopProfile(shop);
      } catch (err) {
        console.error("[artistProfile] load error", err);
        if (!cancelled) setShopProfile(null);
      } finally {
        if (!cancelled) setShopProfileLoading(false);
      }
    }
    loadProfile();
    return () => { cancelled = true; };
  }, []);

  // Scroll listener for back-to-top chevron (noop on mobile — containerRef stays null)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => setShowScrollTop(el.scrollTop > 300);
    el.addEventListener("scroll", onScroll);
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onToggle = (e) => e.detail?.on && setShowPaint(true);
    window.addEventListener("guidepaint:toggle", onToggle);
    return () => window.removeEventListener("guidepaint:toggle", onToggle);
  }, []);

  // Derived values
  const sex = snap.sex === "male" ? "male" : "female";
  const opts = APPEARANCE_OPTIONS[sex];

  const parts = snap.parts || {};
  const activePart = Object.keys(parts).find((k) => parts[k]) || "torso";
  const layersPartKey = snap.viewMode === "fullbody" ? "fullbody" : activePart;

  const allLayers = snap.layers || [];
  const uiLayers = allLayers.filter((L) => {
    const sexOk = !L.sex || L.sex === sex;
    const partOk = L.part
      ? L.part === layersPartKey
      : Array.isArray(L.partKeys)
      ? L.partKeys.includes(layersPartKey)
      : true;
    return sexOk && partOk;
  });

  const hasSomethingToShare = uiLayers.length > 0;
  const viewerReadOnly = !!(readOnly || snap.readOnly);
  const hasShopProfile = !!shopProfile;
  const hairIsNone = (snap.appearance?.hair || "none") === "none";

  const topMode = snap.appearance?.topColorMode || "preset";
  const topPreset = snap.appearance?.topColorPreset || "black";
  const topCustom = snap.appearance?.topColorCustom || "#111111";

  const bottomMode = snap.appearance?.bottomColorMode || "preset";
  const bottomPreset = snap.appearance?.bottomColorPreset || "denim";
  const bottomCustom = snap.appearance?.bottomColorCustom || "#2a3b66";

  const hairMode = snap.appearance?.hairColorMode || "preset";
  const hairPreset = snap.appearance?.hairColorPreset || "brown";
  const hairCustom = snap.appearance?.hairColorCustom || "#3b2a1a";

  function onUploadLayer(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    addLayerFromFile(f);
    if (layerFileRef.current) layerFileRef.current.value = "";
    if (isMobile) goPreview();
  }

  function onUploadDecal(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    addDecalFromFile(f);
    if (decalFileRef.current) decalFileRef.current.value = "";
    if (isMobile) goPreview();
  }

  function setTop(patch) {
    tattoo.appearance = { ...(tattoo.appearance || {}), ...patch };
  }

  function setBottom(patch) {
    tattoo.appearance = { ...(tattoo.appearance || {}), ...patch };
  }

  function setHair(patch) {
    tattoo.appearance = { ...(tattoo.appearance || {}), ...patch };
  }

  // Artist entitlement + gated entry handler.
  // Centralized here so the same logic fires from both desktop and mobile
  // layouts. Flow when the user clicks "Become a Tattune artist":
  //   1. If already entitled (paid sub OR live artist trial): open wizard.
  //   2. If artist trial never started: stamp trial start via RPC, toast,
  //      open wizard. One-way door — they just used their 30 free days.
  //   3. Otherwise (trial ended, no sub): show the artist upgrade modal.
  const {
    isEntitled,
    artistTrialStarted,
    artistTrialDaysLeft,
    startArtistTrial,
  } = useSubscription();

  // True when the user already has a shop row but their artist entitlement
  // has lapsed (no active artist_pro sub AND no live trial). The button
  // label uses this to switch from "Edit studio profile" → "Reactivate
  // studio profile" so the lapsed user understands clicking will route
  // them through the upgrade flow, not straight to the editor. Computed
  // here (after useSubscription) so isEntitled is in scope.
  const shopNeedsReactivation =
    hasShopProfile && !isEntitled("artist_profile");

  const openArtistWizardGated = useCallback(async () => {
    if (isEntitled("artist_profile")) {
      setShowArtistWizard(true);
      return;
    }

    if (!artistTrialStarted) {
      try {
        await startArtistTrial();
        toast.success(
          `Welcome — your ${ARTIST_TRIAL_DAYS}-day Artist trial just started.`,
          { duration: 5000 }
        );
        setShowArtistWizard(true);
      } catch (err) {
        console.error("[artistGate] startArtistTrial failed", err);
        // The start_artist_trial RPC raises 'customer_pro_subscriber' when
        // the caller has an active Customer Pro sub — they need to upgrade
        // to Artist Pro, not stack a free trial on top. Route them into the
        // upgrade modal where the right CTA already lives.
        if (err?.message === "customer_pro_subscriber") {
          toast(
            "Customer Pro doesn't include artist features — upgrade to Artist Pro to list your shop.",
            { duration: 6000 }
          );
          setShowArtistUpgrade(true);
          return;
        }
        toast.error("Couldn't start your trial. Please try again.");
      }
      return;
    }

    // Trial started but no longer entitled — show upgrade path.
    setShowArtistUpgrade(true);
  }, [isEntitled, artistTrialStarted, startArtistTrial]);

  async function onSubmitArtistProfile(values) {
    try {
      const saved = await upsertOwnShopProfile({
        name: values.studioName || values.displayName,
        displayName: values.displayName,
        // contactEmail goes to the public-facing contact_email column;
        // the auth `email` column is filled in by upsertOwnShopProfile
        // from the signed-in user's auth.email so we don't clobber it.
        contactEmail: values.contactEmail,
        phone: values.phone,
        hours: values.hours,
        city: values.city,
        region: values.region,
        country: values.country,
        tagline: values.tagline,
        bio: values.bio,
        website: values.website,
        instagram: values.instagram,
        styles: values.styleTags || [],
        acceptPoolRequests: !!values.acceptPoolRequests,
        // Only persist when present — undefined leaves the saved value alone.
        ...(values.poolNotificationMode
          ? { poolNotificationMode: values.poolNotificationMode }
          : {}),
        street: values.street,
        postalCode: values.postalCode,
      });
      setShopProfile(saved);
      setShowArtistWizard(false);
    } catch (err) {
      console.error("[artistProfile] save error", err);
      toast.error("Couldn't save your studio profile. Check the console for details.");
    }
  }

  return {
    snap,
    partKey,
    layerFileRef,
    decalFileRef,
    containerRef,
    showPaint, setShowPaint,
    showTemplates, setShowTemplates,
    showScrollTop,
    quoteOpen, setQuoteOpen,
    showJoin, setShowJoin,
    showArtistWizard, setShowArtistWizard,
    showArtistUpgrade, setShowArtistUpgrade,
    openArtistWizardGated,
    artistTrialStarted,
    artistTrialDaysLeft,
    shopProfile, setShopProfile,
    shopProfileLoading,
    preparedVideo, setPreparedVideo,
    videoBusy, setVideoBusy,
    videoPreset, setVideoPreset,
    skinOpen, setSkinOpen,
    appearanceOpen, setAppearanceOpen,
    sex,
    opts,
    layersPartKey,
    uiLayers,
    hasSomethingToShare,
    viewerReadOnly,
    hasShopProfile,
    shopNeedsReactivation,
    hairIsNone,
    topMode, topPreset, topCustom,
    bottomMode, bottomPreset, bottomCustom,
    hairMode, hairPreset, hairCustom,
    onUploadLayer,
    onUploadDecal,
    setTop,
    setBottom,
    setHair,
    onSubmitArtistProfile,
  };
}
